import { InlineLoading, InlineNotification } from '@carbon/react';
import { ProvideRootResource } from 'components/hydrate-atoms/hydrate-atoms';
import { api } from 'lib/request/api';
import { useCallback, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { AssetTearsheet } from './asset-tearsheet';
import {
  creatingSummary,
  isBatchActive,
  resultsSummary,
  retryRequestRows,
  skippedCount,
} from './batch';
import { BulkMap } from './bulk-map';
import { BULK_UPLOADS_LIST_PATH } from './bulk-uploads-table';
import styles from './bulk-upload.module.scss';
import { RequestFailure, requestFailure, useBatch } from './hooks';
import { batchMapRow, resolvedLocationId } from './map';
import { RequestFailureBanner } from './request-failure-banner';
import { ResultsTable } from './results-table';
import {
  BulkUploadShell,
  CREATING_ACTIONS,
  FooterAction,
  useCreatePermission,
} from './shell';
import type { BatchRow, BulkAssetBatch, RowValues } from './types';

// Retrying creates tracts under the retrier's own Salesforce credentials.
const NO_SALESFORCE_RETRY =
  "You can't retry: your account can't connect to Salesforce. Ask someone with Salesforce access to retry these assets";

/**
 * One submitted batch at /crm/tracts_and_subtracts/bulk_uploads/:id, from the
 * server alone: Creating while it runs (rows update live), then the results,
 * with Retry failed assets for anyone with Salesforce access.
 * @return {JSX.Element} The page
 */
export const BatchResultsContent = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const permission = useCreatePermission();
  const { batch, error, replace, pollingStopped } = useBatch(Number(id));
  const [edits, setEdits] = useState<Record<number, RowValues>>({});
  const [editing, setEditing] = useState<BatchRow | null>(null);
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [retrying, setRetrying] = useState(false);

  const mapRows = useMemo(
    () =>
      (batch?.rows ?? []).map((row) =>
        batchMapRow(row, edits[row.id] ?? row.payload)
      ),
    [batch, edits]
  );

  const running = !!batch && (isBatchActive(batch.status) || retrying);
  const retryRows = batch ? retryRequestRows(batch.rows, edits) : [];
  let retryBlocker: string | null = null;
  if (batch && !permission.loading && !permission.allowed) {
    retryBlocker = NO_SALESFORCE_RETRY;
  } else if (running) {
    retryBlocker = 'Wait for the batch to finish';
  }

  const retry = async (current: BulkAssetBatch) => {
    setFailure(null);
    setRetrying(true);
    try {
      const next: BulkAssetBatch = await (
        await api.post(`crm/bulk_asset_batches/${current.id}/retry`, {
          json: { rows: retryRequestRows(current.rows, edits) },
        })
      ).json();
      replace(next);
      setEdits({});
    } catch (retryError) {
      setFailure(await requestFailure(retryError));
    } finally {
      setRetrying(false);
    }
  };

  const onEdit = useCallback((row: BatchRow) => setEditing(row), []);
  const close = () => history.push(BULK_UPLOADS_LIST_PATH);

  const footerActions = (): FooterAction[] => {
    if (running) return CREATING_ACTIONS;
    if (!batch || retryRows.length === 0) {
      return [
        { key: 'close', kind: 'primary', label: 'Close', onClick: close },
      ];
    }
    return [
      { key: 'close', kind: 'secondary', label: 'Close', onClick: close },
      {
        key: 'retry',
        kind: 'primary',
        label: 'Retry failed assets',
        disabled: !permission.allowed,
        onClick: () => retry(batch),
      },
    ];
  };

  const renderBody = () => {
    if (!batch) {
      return error ? (
        <InlineNotification kind="error" hideCloseButton title={error} />
      ) : (
        <InlineLoading description="Loading the bulk upload..." />
      );
    }
    return (
      <div className={styles.step}>
        {error && pollingStopped && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={error}
          />
        )}
        {failure && (
          <RequestFailureBanner
            failure={failure}
            action="retried"
            onTryAgain={() => retry(batch)}
          />
        )}
        {!running && retryBlocker && retryRows.length > 0 && (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            title={`${retryBlocker}.`}
          />
        )}
        <p className={styles.summary}>
          {running
            ? `${
                batch.status === 'pending' ? 'Waiting to start. ' : ''
              }${creatingSummary(batch.rowCount, skippedCount(batch))}`
            : resultsSummary(batch)}
        </p>
        <BulkMap rows={mapRows} />
        <ResultsTable
          rows={batch.rows}
          edits={edits}
          retryBlocker={retryBlocker}
          onEdit={onEdit}
        />
      </div>
    );
  };

  const rowErrors =
    editing && failure?.kind === 'rows'
      ? failure.rows.find((row) => row.id === editing.id)?.errors
      : undefined;

  const editingValues = editing && (edits[editing.id] ?? editing.payload);

  return (
    <BulkUploadShell
      results={!!batch && !running}
      hasChanges={Object.keys(edits).length > 0}
      requirePermission={false}
      crumbPath={BULK_UPLOADS_LIST_PATH}
      actions={footerActions()}
    >
      {renderBody()}
      {editing && editingValues && (
        <AssetTearsheet
          key={editing.id}
          editor={{
            mode: 'retry',
            values: editingValues,
            errors: rowErrors ?? {},
            referenceLocationId: resolvedLocationId(
              editingValues,
              editing.payload,
              editing.referenceLocationId
            ),
            onSave: (values) =>
              setEdits((current) => ({ ...current, [editing.id]: values })),
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </BulkUploadShell>
  );
};

/**
 * Mounts the results in their own jotai store, afresh for each batch id, so
 * moving to another batch on this same route never shows the last one's state.
 * @return {JSX.Element} The page
 */
const BatchResultsPage = () => {
  const { id } = useParams<{ id: string }>();
  return (
    <ProvideRootResource key={id}>
      <BatchResultsContent />
    </ProvideRootResource>
  );
};

export default BatchResultsPage;
